import React, { useCallback, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { KeyboardTimePickerProps } from '@material-ui/pickers';
import { isValidDate } from '@utmost.co/utmost-design-system';
import { ComponentPropBase } from '../types';
import { TimePickerAtom } from '../atoms/TimePickerAtom/TimePickerAtom';
import { objectUtil } from '../forms/utils/objectUtil';
import { parseDate } from '../forms/utils/dateHelpers';
import { errorValue } from '../atoms/types';

type Props = ComponentPropBase &
  Partial<Omit<KeyboardTimePickerProps, 'onChange'>> & {
    placeholder?: string;
    onChange?: (e: React.ChangeEvent<any>, setValue: any) => void;
    keyboard?: boolean;
    mapInputValue?: (value: any) => string;
  };

const defaults: Partial<Props> = {
  fullWidth: true,
  clearable: true,
  autoOk: true,
  keyboard: true,
  invalidLabel: ''
};

export const TimePicker: React.FC<Props> = ({
  label,
  placeholder,
  errors,
  helpText,
  touched,
  type,
  disabled,
  required,
  onChange,
  invalidDateMessage,
  onBlur,
  name,
  mapInputValue,
  ...rest
}) => {
  const [input, meta, helpers] = useField(name || 'INVALID');
  const formikContext = useFormikContext();
  const [error, setError] = useState<string | React.ReactNode | null>(null);

  const fieldError = error || meta.error || errorValue(objectUtil.getIn(errors, name));

  const inputValue = mapInputValue ? mapInputValue(input.value) : input.value;

  const value = useMemo(() => {
    const today = new Date();

    return inputValue
      ? parseDate(
          new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDay(),
            inputValue.slice(0, 2),
            inputValue.slice(3, 5)
          )
        )
      : null;
  }, [inputValue]);

  const handleOnChange = useCallback(
    (date: any) => {
      if (!isValidDate(date)) {
        helpers.setTouched(true);
        setError(invalidDateMessage || 'Invalid date');
        if (onChange) {
          // @ts-ignore: TODO SD-11228
          onChange(null, helpers.setValue);
        } else {
          helpers.setValue(null);
        }
      } else {
        helpers.setTouched(true);
        setError(null);
        if (onChange) {
          // @ts-ignore: TODO SD-11228
          onChange(date, helpers.setValue);
        } else {
          helpers.setValue(date);
        }
      }
    },
    [helpers, invalidDateMessage, onChange]
  );

  const handleOnBlur = useCallback(
    (event: any) => {
      if (onBlur) {
        onBlur(event);
      }
      helpers.setTouched(true);
      input.onBlur(event);
    },
    [helpers, input, onBlur]
  );

  const hasSubmitted = formikContext.submitCount > 0;

  return (
    <div data-test={`${name}-field-control`} className="field-control">
      <TimePickerAtom
        id={input.name}
        {...defaults}
        {...rest}
        data-test={`${name}-field`}
        placeholder={placeholder}
        value={value}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        required={required}
        name={name}
        label={label}
        helperText={hasSubmitted && fieldError ? fieldError : helpText}
        disabled={disabled}
        error={hasSubmitted && !!fieldError}
      />
    </div>
  );
};
